import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { TRACK_LABELS } from '@/lib/tracks';
import {
  Lock,
  Unlock,
  Sparkles,
  BookText,
  TestTube,
  ClipboardList,
  ChevronRight,
  ShoppingCart,
  Gift,
  ArrowLeft,
  MessageCircleQuestion,
  FileDown,
  ExternalLink,
} from 'lucide-react';

export default function ChapterHubPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const track = sp.get('track');
  const klass = Number(sp.get('klass'));
  const subject = sp.get('subject');
  const chapter = sp.get('chapter');

  const [access, setAccess] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [derivs, setDerivs] = React.useState(null);
  const [practical, setPractical] = React.useState(null); // {practical_name, explanation, viva}
  const [practicalName, setPracticalName] = React.useState('');
  const [set3, setSet3] = React.useState(null); // { available, locked, drive_url }
  const [busy, setBusy] = React.useState(false);

  const isPractical = track === 'BOARDS_PRACTICAL';
  const isSet3Track = track === 'JEE' || track === 'NEET';

  // Look up practical name up-front so header shows friendly label even when locked
  React.useEffect(() => {
    if (!isPractical || !klass || !subject || !chapter) return;
    api
      .get('/practicals', { params: { klass, subject } })
      .then(({ data }) => {
        const found = (data.practicals || []).find((p) => p.id === chapter);
        if (found) setPracticalName(found.name);
      })
      .catch(() => {});
  }, [isPractical, klass, subject, chapter]);

  const loadAccess = React.useCallback(async () => {
    try {
      const { data } = await api.get('/access/check', {
        params: { track, klass, subject, chapter },
      });
      setAccess(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to check access');
      setAccess({ unlocked: false, reason: 'locked' });
      return null;
    }
  }, [track, klass, subject, chapter]);

  const loadContent = React.useCallback(
    async (state) => {
      if (!state?.unlocked) return;
      if (isPractical) {
        try {
          const { data } = await api.get('/practicals/detail', {
            params: { klass, subject, practical_id: chapter },
          });
          setPractical(data);
        } catch (e) {
          if (e?.response?.status !== 402)
            toast.error(e?.response?.data?.detail || 'Failed to load practical');
        }
      } else {
        try {
          const { data } = await api.get('/study/derivations', {
            params: { track, klass, subject, chapter },
          });
          setDerivs(data);
        } catch (e) {
          if (e?.response?.status !== 402) toast.error('Failed to load checklist');
        }
        if (isSet3Track) {
          try {
            const { data } = await api.get('/set3', {
              params: { track, klass, subject, chapter },
            });
            setSet3(data);
          } catch {
            setSet3(null);
          }
        }
      }
    },
    [isPractical, isSet3Track, track, klass, subject, chapter]
  );

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      const a = await loadAccess();
      if (a) await loadContent(a);
      setLoading(false);
    })();
  }, [loadAccess, loadContent]);

  const claimFree = async () => {
    setBusy(true);
    try {
      await api.post('/access/claim-free', { track, chapter });
      toast.success('Free chapter unlocked!');
      const a = await loadAccess();
      if (a) await loadContent(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unlock');
    } finally {
      setBusy(false);
    }
  };

  const redeemBonus = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/referral/redeem', { track, klass, subject, chapter });
      toast.success(`Chapter unlocked with referral credit! ${data.bonus_credits_remaining} credit(s) left.`);
      const a = await loadAccess();
      if (a) await loadContent(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not redeem credit');
    } finally {
      setBusy(false);
    }
  };

  const goPricing = () => nav('/pricing');
  const startTest = (setNumber) => {
    const params = new URLSearchParams({
      track,
      klass: String(klass),
      subject,
      chapter,
      set_number: String(setNumber),
    });
    nav(`/pre-test?${params.toString()}`);
  };

  const toggleDerivation = async (item, done) => {
    // optimistic update
    setDerivs((prev) => {
      if (!prev) return prev;
      const set = new Set(prev.completed_ids || []);
      if (done) set.add(item.id);
      else set.delete(item.id);
      const completed_ids = Array.from(set);
      const doneCount = prev.items.filter((it) => set.has(it.id)).length;
      return { ...prev, completed_ids, done: doneCount };
    });
    try {
      await api.post('/study/derivations/toggle', {
        klass,
        subject,
        chapter,
        item_id: item.id,
        done,
      });
    } catch (e) {
      toast.error('Failed to save');
      // revert
      loadContent(access);
    }
  };

  return (
    <AppShell>
      <div className="space-y-5" data-testid="chapter-hub-page">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="gap-1.5" data-testid="hub-back-button">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>

        <section>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="secondary">{TRACK_LABELS[track] || track}</Badge>
            <Badge variant="secondary">Class {klass}</Badge>
            <Badge variant="secondary">{subject}</Badge>
          </div>
          <h1 className="mt-2 font-display text-3xl font-semibold">
            {isPractical ? (practical?.practical_name || practicalName || chapter) : chapter}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isPractical
              ? 'Complete practical explanation + 12 viva questions with model answers.'
              : 'Take a 35-question fixed test set, or track derivations mastery below.'}
          </p>
        </section>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        ) : !access?.unlocked ? (
          <LockedPanel
            access={access}
            onClaimFree={claimFree}
            onRedeemBonus={redeemBonus}
            onBuy={goPricing}
            busy={busy}
            isPractical={isPractical}
          />
        ) : (
          <>
            {isPractical ? (
              <PracticalPanel practical={practical} />
            ) : (
              <>
                <div
                  className={`grid gap-4 ${
                    isSet3Track && set3?.available ? 'md:grid-cols-3' : 'md:grid-cols-2'
                  }`}
                >
                  <TestSetCard setNumber={1} onStart={() => startTest(1)} />
                  <TestSetCard setNumber={2} onStart={() => startTest(2)} />
                  {isSet3Track && set3?.available && (
                    <Set3Card track={track} driveUrl={set3.drive_url} locked={set3.locked} />
                  )}
                </div>
                <DerivationsPanel data={derivs} onToggle={toggleDerivation} />
              </>
            )}

            {access?.reason === 'free_chapter' && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
                <div className="flex items-start gap-3">
                  <Gift className="mt-0.5 h-5 w-5 text-red-500" />
                  <div className="flex-1">
                    <div className="font-semibold">This is your free chapter for {TRACK_LABELS[track] || track}.</div>
                    <p className="text-sm text-muted-foreground">
                      Loving the vibe? Unlock every chapter with a plan.
                    </p>
                  </div>
                  <Button onClick={goPricing} className="gap-1.5 bg-red-600 hover:bg-red-500" data-testid="hub-buy-plan">
                    <ShoppingCart className="h-4 w-4" />
                    Buy plan
                  </Button>
                </div>
              </div>
            )}
            {access?.reason === 'bonus_unlocked' && (
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4">
                <div className="flex items-start gap-3">
                  <Gift className="mt-0.5 h-5 w-5 text-emerald-600" />
                  <div className="flex-1">
                    <div className="font-semibold">Unlocked via referral credit 🎉</div>
                    <p className="text-sm text-muted-foreground">
                      Nice! Keep the referrals coming — every friend who buys a plan gives you another bonus chapter.
                    </p>
                  </div>
                  <Button variant="outline" onClick={() => nav('/referral')} className="gap-1.5" data-testid="hub-open-referral">
                    Refer & Earn
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function LockedPanel({ access, onClaimFree, onRedeemBonus, onBuy, busy, isPractical }) {
  const canClaim = access?.can_claim_free;
  const canRedeem = access?.can_redeem_bonus;
  const credits = access?.bonus_credits || 0;
  const otherFree = access?.free_chapter_for_section;
  return (
    <Card className="border-red-500/30">
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-lg bg-red-100 text-red-600">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold">Chapter locked</div>
            <div className="text-sm text-muted-foreground">
              {canClaim
                ? 'Every section gives you 1 free chapter. Claim it here to start.'
                : otherFree
                ? `Your free chapter for this section is already set to “${otherFree}”. Buy a plan or use a referral credit to unlock more.`
                : 'Buy a plan or use a referral credit to unlock this chapter.'}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canClaim && (
            <Button onClick={onClaimFree} disabled={busy} className="gap-1.5 bg-red-600 hover:bg-red-500" data-testid="claim-free-button">
              <Gift className="h-4 w-4" />
              {busy ? 'Unlocking…' : 'Use as my free chapter'}
            </Button>
          )}
          {canRedeem && (
            <Button
              onClick={onRedeemBonus}
              disabled={busy}
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-500"
              data-testid="redeem-referral-button"
            >
              <Gift className="h-4 w-4" />
              {busy ? 'Redeeming…' : `Redeem referral credit (${credits} left)`}
            </Button>
          )}
          <Button variant="outline" onClick={onBuy} className="gap-1.5" data-testid="locked-buy-plan">
            <ShoppingCart className="h-4 w-4" />
            See plans (₹999 / year)
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {isPractical
            ? 'Practicals plan unlocks all chapters’ viva Q&A. Or grab the All-Access bundle for ₹1,899 / year.'
            : 'Section plan unlocks all chapters in this track. Or grab the All-Access bundle for ₹1,899 / year.'}
        </p>
      </CardContent>
    </Card>
  );
}

function TestSetCard({ setNumber, onStart }) {
  return (
    <Card className="group transition-shadow hover:shadow-md">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-lg bg-red-600 text-white shadow-[0_0_20px_rgba(239,68,68,0.35)]">
            <BookText className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold">Set {setNumber}</div>
            <div className="text-xs text-muted-foreground">35 fixed MCQs · Live stopwatch · AI report card</div>
          </div>
        </div>
        <Button className="w-full gap-1.5" onClick={onStart} data-testid={`hub-start-set-${setNumber}`}>
          <Sparkles className="h-4 w-4" />
          Start Set {setNumber}
        </Button>
      </CardContent>
    </Card>
  );
}

function Set3Card({ track, driveUrl, locked }) {
  const open = () => {
    if (!driveUrl) return;
    window.open(driveUrl, '_blank', 'noopener,noreferrer');
  };
  return (
    <Card
      className="group border-red-500/25 bg-gradient-to-br from-red-500/10 via-transparent to-transparent transition-shadow hover:shadow-md"
      data-testid="set3-card"
    >
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-lg bg-red-600 text-white shadow-[0_0_20px_rgba(239,68,68,0.35)]">
            <FileDown className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <div className="font-display text-lg font-semibold">Set 3</div>
              <Badge variant="outline" className="border-red-500/40 text-[10px] uppercase tracking-wide text-red-500">
                {track} chapterwise PDF
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              Curated PYQ / module PDF opens in a new tab.
            </div>
          </div>
        </div>
        {locked ? (
          <Button
            variant="outline"
            className="w-full gap-1.5"
            disabled
            data-testid="hub-set3-locked"
          >
            <Lock className="h-4 w-4" />
            Unlock chapter to open
          </Button>
        ) : (
          <Button
            onClick={open}
            className="w-full gap-1.5 bg-red-600 hover:bg-red-500"
            data-testid="hub-open-set3"
          >
            <ExternalLink className="h-4 w-4" />
            View Set 3
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function DerivationsPanel({ data, onToggle }) {
  // Hooks must be called unconditionally, BEFORE any early return.
  const grouped = React.useMemo(() => {
    const groups = [];
    const idx = new Map();
    for (const it of (data && data.items) || []) {
      const key = (it.section || '').trim() || 'Core';
      if (!idx.has(key)) {
        idx.set(key, groups.length);
        groups.push({ section: key, items: [] });
      }
      groups[idx.get(key)].items.push(it);
    }
    return groups;
  }, [data]);

  if (!data) {
    return (
      <Card>
        <CardContent className="space-y-2 p-5">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            <div className="font-display text-lg font-semibold">Derivations & Concepts checklist</div>
          </div>
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }
  const percent = data.total ? Math.round((data.done / data.total) * 100) : 0;
  const completedSet = new Set(data.completed_ids || []);

  return (
    <Card data-testid="derivations-panel">
      <CardContent className="space-y-4 p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            <div className="font-display text-lg font-semibold">Derivations & Concepts checklist</div>
          </div>
          <Badge variant="secondary" className="font-mono-ui" data-testid="derivations-count">
            {data.done} / {data.total}
          </Badge>
        </div>
        <div>
          <div className="mb-1 text-xs text-muted-foreground">Mastery {percent}%</div>
          <Progress value={percent} />
        </div>
        <div className="space-y-5">
          {grouped.map((group) => {
            const groupDone = group.items.filter((i) => completedSet.has(i.id)).length;
            return (
              <div key={group.section} data-testid={`derivation-group-${group.section}`}>
                <div className="mb-2 flex items-baseline justify-between border-b border-border/60 pb-1">
                  <div className="text-sm font-semibold uppercase tracking-wide text-primary/90">
                    {group.section}
                  </div>
                  <div className="font-mono-ui text-[11px] text-muted-foreground">
                    {groupDone} / {group.items.length}
                  </div>
                </div>
                <ul className="space-y-2">
                  {group.items.map((it) => {
                    const done = completedSet.has(it.id);
                    return (
                      <li
                        key={it.id}
                        className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                          done ? 'border-emerald-500/40 bg-emerald-500/5' : 'hover:bg-secondary/40'
                        }`}
                        data-testid={`derivation-item-${it.id}`}
                      >
                        <Checkbox
                          checked={done}
                          onCheckedChange={(v) => onToggle(it, !!v)}
                          className="mt-1"
                          data-testid={`derivation-check-${it.id}`}
                        />
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`font-medium ${done ? 'line-through text-muted-foreground' : ''}`}>
                              {it.title}
                            </span>
                            <Badge variant="outline" className="text-[10px]">
                              {it.category}
                            </Badge>
                          </div>
                          {it.summary && <div className="mt-0.5 text-sm text-muted-foreground">{it.summary}</div>}
                          {it.why_important && (
                            <div className="mt-1 text-xs italic text-muted-foreground">Why: {it.why_important}</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function PracticalPanel({ practical }) {
  if (!practical) {
    return (
      <Card>
        <CardContent className="space-y-2 p-5">
          <div className="flex items-center gap-2">
            <TestTube className="h-5 w-5 text-primary" />
            <div className="font-display text-lg font-semibold">Practical</div>
          </div>
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <div className="text-xs text-muted-foreground">Loading (first time may take ~20s)…</div>
        </CardContent>
      </Card>
    );
  }

  const e = practical.explanation || {};
  const viva = practical.viva || [];

  return (
    <div className="space-y-4">
      {/* Explanation */}
      <Card data-testid="practical-explanation">
        <CardContent className="space-y-5 p-5 sm:p-6">
          <div className="flex items-center gap-2">
            <TestTube className="h-5 w-5 text-primary" />
            <div className="font-display text-lg font-semibold">Practical explanation</div>
          </div>

          {e.aim && (
            <Section title="Aim">
              <p className="text-sm">{e.aim}</p>
            </Section>
          )}
          {e.apparatus?.length > 0 && (
            <Section title="Apparatus required">
              <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {e.apparatus.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="mt-2 inline-block h-1.5 w-1.5 flex-none rounded-full bg-red-500" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {e.principle && (
            <Section title="Principle">
              <p className="text-sm leading-relaxed">{e.principle}</p>
            </Section>
          )}
          {e.procedure?.length > 0 && (
            <Section title="Procedure">
              <ol className="space-y-2 text-sm">
                {e.procedure.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-red-600 text-xs font-semibold text-white">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </Section>
          )}
          {e.observations?.length > 0 && (
            <Section title="Observations">
              <ul className="list-inside list-disc space-y-1 text-sm">
                {e.observations.map((o, i) => (
                  <li key={i}>{o}</li>
                ))}
              </ul>
            </Section>
          )}
          {e.precautions?.length > 0 && (
            <Section title="Precautions">
              <ul className="list-inside list-disc space-y-1 text-sm">
                {e.precautions.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </Section>
          )}
          {e.sources_of_error?.length > 0 && (
            <Section title="Sources of error">
              <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                {e.sources_of_error.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </Section>
          )}
        </CardContent>
      </Card>

      {/* Viva */}
      <Card data-testid="viva-panel">
        <CardContent className="space-y-4 p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircleQuestion className="h-5 w-5 text-primary" />
              <div className="font-display text-lg font-semibold">Viva Q&amp;A</div>
            </div>
            <Badge variant="secondary" className="font-mono-ui">
              {viva.length} questions
            </Badge>
          </div>
          <ul className="space-y-3">
            {viva.map((it, i) => (
              <li key={it.id} className="rounded-lg border p-4" data-testid={`viva-item-${it.id}`}>
                <div className="flex items-start gap-3">
                  <div className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-red-600 text-xs font-semibold text-white">
                    Q{i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{it.q}</div>
                    <div className="mt-2 rounded-md border bg-secondary/40 p-3 text-sm">
                      <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Answer:
                      </span>
                      {it.a}
                    </div>
                    {it.tip && (
                      <div className="mt-2 text-xs italic text-muted-foreground">
                        <span className="font-semibold not-italic text-foreground">Tip:</span> {it.tip}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
const _unused = [Unlock, ChevronRight];
